<template>
  <div class="course-video-player">
      <!-- :poster="currentChapter && currentChapter.chapterBackgroundImg" -->
    <base-video-player
      ref="baseVideoPlayer"
      :src="currentVideoUrl"
      :current-index="currentIndex"
      :chapters="chapters"
      :title="chapterTitle"
      :loading="videoLoading"
      :disable-prev="currentIndex <= 0"
      :disable-next="currentIndex >= chapters.length - 1"
      :features="mergedFeatures"
      :resume-time="resumeTime"
      :show-resume-tip="showResumeTip"
      @prev="playPrev"
      @next="playNext"
      @play="handlePlaybackStart"
      @pause="handlePlaybackPause"
      @ended="handleEnded"
      @timeupdate="handleTimeUpdate"
      @seek="handleSeek"
      @resume="handleResume"
      @fullscreenchange="handleFullscreenChange"
      @selectChapter="handleSelectChapter"
    />
  </div>
</template>

<script>
import { getCourseVideo, updateCourseProgress } from '@/api/courseData';
import BaseVideoPlayer from '@/components/video/BaseVideoPlayer.vue';

const PROGRESS_INTERVAL = 60; // 每60秒上报一次进度

export default {
  name: 'CourseVideoPlayer',
  components: {
    BaseVideoPlayer,
  },
  props: {
    chapterTitle: {
      type: String,
      default: '',
    },
    chapters: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    features: {
      type: Object,
      default: () => ({}),
    },
    resumeTime: {
      type: Number,
      default: 0,
    },
    showResumeTip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentVideoUrl: '',
      videoLoading: false,
      lastChapterId: null,
      currentTime: 0,
      lastSavedTimestamp: 0, // 保存上一次上报进度的时间戳
      init: true,
    };
  },
  computed: {
    currentChapter() {
      return this.chapters[this.currentIndex] || null;
    },
    mergedFeatures() {
      return {
        prev: true,
        next: true,
        fullscreen: true,
        keyboard: true,
        autoHide: true,
        ...(this.features || {}),
      };
    },
  },
  watch: {
    chapters: {
      handler() {
        this.prepareVideo();
      },
      immediate: true,
    },
    currentIndex() {
      this.prepareVideo();
    },
  },
  methods: {
    prepareVideo() {
      const chapter = this.currentChapter;
      if (!chapter) {
        this.resetPlayer();
        this.resetProgressTracking();
        return;
      }
      if (chapter.chapterId === this.lastChapterId) return;
      this.lastChapterId = chapter.chapterId;
      this.resetProgressTracking();
      this.fetchVideo(chapter).then(() => {
        if (!this.init) {
          this.$nextTick(() => {
            this.$refs.baseVideoPlayer?.togglePlay();
          })
        } else {
          this.init = false;
        }
      });
    },
    resetPlayer() {
      this.currentVideoUrl = '';
      this.videoLoading = false;
      this.currentTime = 0;
      this.resetProgressTracking();
    },
    fetchVideo(chapter) {
      this.videoLoading = true;
      this.currentVideoUrl = '';
      return getCourseVideo(chapter.vcloudObjectName, chapter.vcloudObjectVid)
        .then((res) => {
          this.currentVideoUrl = res.body.videoUrl;
        })
        .finally(() => {
          this.videoLoading = false;
        });
    },
    requestChapterChange(nextIndex) {
      if (nextIndex < 0 || nextIndex >= this.chapters.length) return;
      this.$emit('update:currentIndex', nextIndex);
    },
    playPrev() {
      this.requestChapterChange(this.currentIndex - 1);
    },
    playNext() {
      this.requestChapterChange(this.currentIndex + 1);
    },
    handleSelectChapter(index) {
      this.requestChapterChange(index);
    },
    handleTimeUpdate(time) {
      this.currentTime = time;
      this.$emit('timeupdate', time);
      this.maybeSaveProgressOnInterval(time);
    },
    handleSeek(time) {
      this.saveProgress('seek', time);
      this.$emit('seek', time);
    },
    handleResume(time) {
      this.saveProgress('resume', time);
      this.$emit('resume', time);
    },
    handlePlaybackStart() {
      this.saveProgress('play');
    },
    handlePlaybackPause() {
      this.saveProgress('pause');
      this.lastSavedTimestamp = 0; // 暂停时重置保存时间戳
    },
    handleEnded() {
      this.saveProgress('ended');
      this.playNext();
    },
    handleFullscreenChange(isFullscreen) {
      this.saveProgress(isFullscreen ? 'enter_fullscreen' : 'exit_fullscreen');
      this.$emit('fullscreenchange', isFullscreen);
    },
    resetProgressTracking() {
      this.lastSavedTimestamp = 0;
    },
    maybeSaveProgressOnInterval(time) {
      if (!this.lastSavedTimestamp) return;
      const now = Date.now();
      if (now - this.lastSavedTimestamp < PROGRESS_INTERVAL * 1000) return;
      this.saveProgress('interval', time);
    },
    saveProgress(trigger, fragmentOverride) {

      const chapter = this.currentChapter;
      if (!chapter) return;

      const fragment = Math.floor(
        typeof fragmentOverride === 'number' ? fragmentOverride : this.currentTime
      );
      console.log('saveProgress', trigger, fragment);
      if (fragment < 0) return;
      const now = Date.now();
      const interval = this.lastSavedTimestamp ? Math.floor((now - this.lastSavedTimestamp)/1000) : 0;
      this.lastSavedTimestamp = now;
      updateCourseProgress({
        bookId: chapter.bookId,
        chapterId: chapter.chapterId,
        booklibraryId: Number(this.$route?.query?.libraryId),
        fragment,
        interval,
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(`[CourseVideoPlayer] Failed to save progress (${trigger})`, error);
      });
    },
  },
};
</script>

<style scoped>
.course-video-player {
  width: 100%;
  height: 100%;
}
</style>

